import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase.js';

// ResponderAIChat — on-site AI assistant for technicians (Task #103, AI-RESPONDER).
//
// Opens from the AssignedCard "Ask the AI" button. Modal panel — covers the
// screen on mobile, sized panel on desktop. Each question goes to the
// responderAiChat Cloud Function which RAG-retrieves chunks from the vendor's
// uploaded manuals (Task #104 knowledge base) and asks Claude Haiku.
//
// State is local-only — we don't persist the chat transcript anywhere on the
// server. Each turn resends the conversation array. Closing the panel
// discards it. Spec §4 — citations come back from the server alongside the
// reply and are rendered as a small list under each AI turn.
//
// Props:
//   ticket — the ticket the responder is asking about (gives ticketId + context)
//   onClose — fires when the user closes the panel

export default function ResponderAIChat({ ticket, onClose, embedded = false }) {
  const [messages, setMessages] = useState([]);     // {role, content, citations?}
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  // Focus the input when the panel opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-scroll to the latest message.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending]);

  async function send() {
    const q = input.trim();
    if (!q || pending) return;
    setInput('');
    setError(null);
    const next = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setPending(true);
    try {
      const fn = httpsCallable(functions, 'responderAiChat');
      const res = await fn({
        ticketId: ticket?.id || null,
        message: q,
        // Send the message history (excluding citations — those are display-only)
        // so the server gets multi-turn context.
        conversation: next.map((m) => ({ role: m.role, content: m.content })),
      });
      const d = res.data || {};
      setMessages([
        ...next,
        { role: 'assistant', content: d.reply || '', citations: d.citations || [] },
      ]);
    } catch (err) {
      setError(friendly(err));
    } finally {
      setPending(false);
      inputRef.current?.focus();
    }
  }

  const outerStyle = embedded
    ? { padding: 0 } // inline: no backdrop, lets the panel flow with the page
    : {
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 12,
      };
  const innerStyle = embedded
    ? {
        width: '100%', maxWidth: 720, margin: '0 auto',
        background: 'white', borderRadius: 12,
        display: 'flex', flexDirection: 'column',
        border: '1px solid var(--border, #e2e8f0)',
        overflow: 'hidden',
        minHeight: '60vh',
      }
    : {
        width: '100%', maxWidth: 560, maxHeight: '92vh',
        background: 'white', borderRadius: 12,
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        overflow: 'hidden',
      };
  return (
    <div
      style={outerStyle}
      onClick={embedded ? undefined : (e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={innerStyle}>
        {/* Header */}
        <div style={{
          padding: '12px 14px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Ask the AI</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              Grounded in your vendor's uploaded manuals
              {ticket?.skillName && <> · {ticket.skillName}</>}
              {ticket?.assetName && <> · {ticket.assetName}</>}
            </div>
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost"
              style={{ fontSize: 14, padding: '4px 10px' }}>
              Close
            </button>
          )}
        </div>

        {/* Transcript */}
        <div
          ref={scrollerRef}
          style={{
            flex: 1, padding: 12, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
          {messages.length === 0 && !pending && (
            <div className="placeholder" style={{ marginTop: 8 }}>
              Ask anything technical about this job. The AI answers using only
              your vendor's uploaded documentation and cites the source.
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
                Example: "What does fault code E04 mean on a Carrier 30RAP?"<br />
                Example: "Recommended torque for the compressor mounting bolts?"
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <Turn key={i} m={m} />
          ))}
          {pending && (
            <div style={{ alignSelf: 'flex-start' }}>
              <div style={{
                padding: '8px 12px', borderRadius: 12,
                background: '#f1f5f9', color: '#0f172a',
                fontSize: 13, fontStyle: 'italic',
              }}>
                Searching the manuals…
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="error-banner" style={{ margin: '0 12px 8px' }}>{error}</div>
        )}

        {/* Composer */}
        <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            style={{ display: 'flex', gap: 8 }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a technical question…"
              disabled={pending}
              maxLength={1500}
              style={{
                flex: 1, padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                fontSize: 14,
              }}
            />
            <button
              type="submit"
              disabled={pending || !input.trim()}
              className="btn-primary"
              style={{ minWidth: 72 }}>
              Send
            </button>
          </form>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
            Answers may be wrong. Always verify against the source doc before acting.
          </div>
        </div>
      </div>
    </div>
  );
}

function Turn({ m }) {
  const isUser = m.role === 'user';
  return (
    <div style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '92%' }}>
      <div style={{
        padding: '8px 12px', borderRadius: 12,
        background: isUser ? 'var(--accent, #4f46e5)' : '#f1f5f9',
        color: isUser ? 'white' : '#0f172a',
        fontSize: 14, lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        {m.content}
      </div>
      {!isUser && Array.isArray(m.citations) && m.citations.length > 0 && (
        <div style={{
          marginTop: 6, padding: 8,
          background: '#f8fafc', borderRadius: 8,
          fontSize: 11, color: '#475569',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Sources ({m.citations.length})
          </div>
          {m.citations.map((c, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 600, color: '#3730a3' }}>
                {c.docName}{c.pageNum ? ` · p.${c.pageNum}` : ''}
              </div>
              <div style={{ marginTop: 2, color: '#475569' }}>
                {c.excerpt}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function friendly(err) {
  if (err?.code === 'failed-precondition') return err.message || 'AI assistant is not available right now.';
  if (err?.code === 'permission-denied') return 'You do not have access to the AI assistant.';
  if (err?.code === 'not-found') return 'Could not find the ticket context.';
  return err?.message || 'Something went wrong.';
}
